/**
 * Prehliadanie odhadzovacieho balíčka. Odhod je v Unmatched verejný, takže
 * počítanie kariet je legitímna súčasť hry — nie je dôvod ho skrývať.
 * Karty sú zoradené od naposledy odhodenej.
 */

import type { CardDef } from '@unmatched/engine';
import { useEffect, useState } from 'react';

import { Card } from './Card.js';
import { CardPreview, type PreviewTarget } from './CardPreview.js';

interface Props {
  title: string;
  sub: string;
  /** V poradí, v akom sa majú ukázať — volajúci ich už otočil. */
  cards: CardDef[];
  /**
   * Výber karty efektom (Battle Hardened: „vyber kartu z odhodu"). `cardIds`
   * idú v rovnakom poradí ako `cards`; klikateľné sú len tie v `pickable`.
   */
  pick?: { cardIds: string[]; pickable: Set<string>; onPick: (cardId: string) => void };
  onClose: () => void;
}

export function PileDialog({ title, sub, cards, pick, onClose }: Props) {
  // Karty v okne sú malé — pod kurzorom sa zväčšia vedľa seba (ako v ruke).
  const [preview, setPreview] = useState<PreviewTarget | null>(null);
  const hover = (def: CardDef | null, rect?: DOMRect) =>
    setPreview(def && rect ? { def, rect, place: 'beside' } : null);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="modal" role="dialog" aria-modal="true" onClick={onClose}>
      <div className="modal__panel" onClick={(event) => event.stopPropagation()}>
        <header className="modal__head">
          <div>
            <h2 className="modal__title">{title}</h2>
            <span className="modal__sub">{sub}</span>
          </div>
          <button type="button" className="btn btn--ghost" onClick={onClose}>
            Zavrieť
          </button>
        </header>

        <div className="deck__body">
          <div className="deck__cards">
            {cards.map((def, index) => {
              const cardId = pick?.cardIds[index];
              const pickable = Boolean(cardId && pick?.pickable.has(cardId));
              return (
                <div key={`${def.id}-${index}`} className={`deckCard${pickable ? ' is-pickable' : ''}`}>
                  <Card
                    def={def}
                    disabled={Boolean(pick) && !pickable}
                    onClick={pickable && cardId ? () => pick!.onPick(cardId) : undefined}
                    onHover={hover}
                  />
                  {index === 0 ? <span className="deckCard__qty">vrch</span> : null}
                </div>
              );
            })}
            {cards.length === 0 ? <p className="deck__warning">Zatiaľ prázdny.</p> : null}
          </div>
        </div>
      </div>
      <CardPreview target={preview} />
    </div>
  );
}
